import { useRef, useState, useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Document } from "../../../../themes/Default/components/Form/components/Document";
import { Documents as DocumentsWrapper } from "../../../../themes/Default/components/Documents";
import { Item } from "../../../../themes/Default/components/Documents/components/Item";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";
import Toaster from "../../../../themes/Default/components/Toaster/Toaster";

const Documents = () => {
    const formRef = useRef(null);
    const { studentId } = useParams();
    const [student, setStudent] = useState();
    const { t } = useTranslation("common");
    const [documents, setDocuments] = useState([]);
    const navigate = useNavigate();
    const { useAddDocumentMutation, useListDocumentsMutation, useDeleteDocumentMutation, useDownloadDocumentMutation, useViewStudentsMutation } = studentsManagement;
    const [addDocument] = useAddDocumentMutation();
    const [listDocuments] = useListDocumentsMutation();
    const [deleteDocument] = useDeleteDocumentMutation();
    const [downloadDocument] = useDownloadDocumentMutation();
    const [viewStudent] = useViewStudentsMutation();


    const gotoDetailsTab = (studentId) => {
        navigate("/students/edit/" + studentId);
    };

    const gotoTestsTab = (studentId) => {
        navigate('/students/' + studentId + '/tests');
    }

    const gotoGroupsTab = (studentId) => {
        navigate('/students/' + studentId + '/groups');
    }

    const gotoNotesTab = (studentId) => {
        navigate("/students/" + studentId + "/notes");
    };

    const gotoStudentsList = (studentId) => {
        navigate("/students");
    };

    const gotoCertificatesTab = (studentId) => {
        navigate('/students/' + studentId + '/certificates');
    }

    const gotoContractsTab = (studentId) => {
        navigate("/students/" + studentId + "/contracts");
    };

    const gotoCallsTab = (studentId) => {
        navigate("/students/" + studentId + "/calls");
    };

    const tabs = [
        { title: t("details"),onClick: () => gotoDetailsTab(studentId) },
        // { title: t('students.tests'), onClick: () => gotoTestsTab(studentId)},
        { title: t("students.contract"), onClick: () => gotoContractsTab(studentId) },
        { title: t("calls.title"), onClick: () => gotoCallsTab(studentId) },
        { title: t("groups.title"),  onClick: () => gotoGroupsTab(studentId) },
        { title: t('students.certificates'), onClick: () => gotoCertificatesTab(studentId)},
        { title: t("documents"),  isActive: true },
        { title: t("notes"),  onClick: () => gotoNotesTab(studentId) },
    ];

    const onDocumentDelete = (event, documentId) => {
        event.preventDefault();
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteDocument({ studentId, documentId }).then((result) => {
                            toast.info(result?.data?.info?.responseMessage);
                            viewDocuments();
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onDocumentDownload = async (event, documentId) => {
        event.preventDefault();
        const blob = await downloadDocument({ studentId, documentId }).unwrap();

        const docToDownload = documents.find((doc) => doc.id === documentId);
        let fileName = docToDownload?.fileName ?? "dokument";
        if (docToDownload?.extension) {
            fileName += '.' + docToDownload.extension;
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(urlBlob);
    };

    const viewDocuments = () => {
        listDocuments(studentId).then((result) => {
            const documents = result?.data?.records;
            setDocuments(documents);
        });
    };

    useEffect(() => {
        viewDocuments();
    }, []);

    useEffect(() => {
        viewStudent(studentId).then((result) => {
            setStudent(result?.data);
        });
    }, []);

    const truncateWithTooltip = (text, maxLength = 25) => {
        if (!text) return "";
        const shouldTruncate = text.length > maxLength;
        const truncated = shouldTruncate ? text.slice(0, maxLength) + "..." : text;

        return <span title={shouldTruncate ? text : ""}>{truncated}</span>;
    };

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/students" />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{student?.firstName} {student?.lastName}</Title>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("documents")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        document: null,
                    }}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Document
                                        name="document"
                                        onChange={(event) => {
                                            const file = event.currentTarget.files[0];
                                            setFieldValue("document", file);
                                            addDocument({ file, studentId }).then((result) => {
                                                if (result?.error) {
                                                    toast.error(result.error.data.error.responseMessage);
                                                } else {
                                                    toast.info(result?.data?.info?.responseMessage);
                                                    listDocuments(studentId).then((result) => {
                                                        const documents = result?.data?.records;
                                                        setDocuments(documents);
                                                    });
                                                }
                                            });
                                        }}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <DocumentsWrapper>
                                        {documents?.length > 0 &&
                                            documents?.map((document, index) => {
                                                return (
                                                    <Item
                                                        id={document.id}
                                                        key={index}
                                                        name={truncateWithTooltip(document.fileName)}
                                                        type={document.mimeType}
                                                        onDelete={(event) => onDocumentDelete(event, document.id)}
                                                        onDownload={(event) => onDocumentDownload(event, document.id)}
                                                    />
                                                );
                                            })}
                                    </DocumentsWrapper>
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default Documents;
